import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';

// APIs
import {
  getIndividualInventoryOrder,
  updateInventoryOrderStatus,
} from '../../../../api/admin/orders/orders';

// Components
import CentsLoader from '../../../common/centsLoader/CentsLoader';
import {
  KeyDetails,
  PaymentDetails,
  Payments,
  OrderSummary,
  CustomerDetails,
  StoreDetails,
  EditModal,
} from '../components';
import NotFound from 'components/common/NotFound/NotFound';

// Constants
import { inventoryOrderStatuses } from '../../../../utils/constants';

// Utils
import { getIsNotFound } from 'utils/getIsNotFound';

const IndividualInventoryOrder = () => {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [inventoryOrder, setInventoryOrder] = useState({});
  const [error, setError] = useState();
  const [modalType, setModalType] = useState(null);
  const [isNotFound, setIsNotFound] = useState(false);

  // state for updated inventoryOrder fields
  const [updatedStatus, setUpdatedStatus] = useState();

  /**
   * Retrieve order details using incoming URL params
   */
  const getIndividualOrder = useCallback(async () => {
    try {
      const inventoryOrderData = await getIndividualInventoryOrder(id);
      setIsNotFound(false);
      setInventoryOrder(inventoryOrderData.data.inventoryOrder);
      setLoading(false);
    } catch (error) {
      if (getIsNotFound(error)) {
        setIsNotFound(true);
      }
      setLoading(false);
      console.log(error.response.data.error);
      Sentry.captureException(error);
    }
  }, [id]);

  useEffect(() => {
    getIndividualOrder();
  }, [getIndividualOrder]);

  /**
   * Update status based on new selection or selection changes
   *
   * @param {Object} event
   */
  const handleStatusChange = (event) => {
    setError(null);
    setUpdatedStatus(event.value);
  };

  /**
   * Update the ServiceOrder object with the new status selection
   */
  const saveUpdatedStatus = async () => {
    try {
      setLoading(true);

      const payload = {
        inventoryOrderId: inventoryOrder.id,
        status: updatedStatus,
      };
      const updatedInventoryOrderData = await updateInventoryOrderStatus(payload);

      setInventoryOrder(updatedInventoryOrderData.data.inventoryOrder);
      setUpdatedStatus(updatedInventoryOrderData.data.inventoryOrder.status);

      setModalType(null);

      setLoading(false);
    } catch (error) {
      setError(error.response.data.error);
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <CentsLoader />}
      {!loading &&
        (isNotFound ? (
          <NotFound />
        ) : (
          <div className="page-container">
            <div className="inventory-order-page-container">
              <h1 className="inventory-order-header">
                Inventory Order: Code {inventoryOrder.orderCode}
              </h1>
              <div className="inventory-order-content">
                <KeyDetails
                  data={{ ...inventoryOrder, orderType: 'INVENTORY' }}
                  onEdit={setModalType}
                />
                <PaymentDetails data={inventoryOrder} />
                <Payments payments={inventoryOrder.order?.payments} />
                <OrderSummary data={inventoryOrder} />
                <CustomerDetails data={inventoryOrder.centsCustomer} />
                <StoreDetails data={inventoryOrder.store} />
              </div>
            </div>
            <EditModal
              onClose={() => setModalType(null)}
              modalType={modalType}
              currentValue={inventoryOrder.status}
              updatedValue={updatedStatus}
              handleValueChange={handleStatusChange}
              inputType="select"
              selectOptions={inventoryOrderStatuses}
              onSave={saveUpdatedStatus}
              error={error}
            />
          </div>
        ))}
    </>
  );
};

export default IndividualInventoryOrder;
